import { useEffect, useMemo, useState } from "react";
import { useLocation } from "@/lib/browserRouter";
import {
  CHECKOUT_EMBED_SOURCE,
  SALES_BUILDER_SOURCE,
  salesPageApiBase,
} from "./salesPageApi";
import "./sales-page.css";

function money(cents) {
  return ((Number(cents) || 0) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function parentOriginAllowed(origin) {
  try {
    const url = new URL(origin);
    if (url.protocol === "https:" && url.hostname === "app.atomicpay.com.br") return true;
    return url.protocol === "http:" && (url.hostname === "localhost" || url.hostname === "127.0.0.1");
  } catch {
    return false;
  }
}

function embedSrc(code, search) {
  const params = new URLSearchParams(search);
  params.delete("builder");
  params.delete("offer");
  params.set("embed", "1");
  const query = params.toString();
  return `/${encodeURIComponent(code)}${query ? `?${query}` : ""}`;
}

function Block({ block, offers, activeOffer, onOffer, checkoutLinks, search, builder, selectedId, onSelect }) {
  const selected = builder && selectedId === block.id ? " is-selected" : "";
  const select = () => onSelect?.(block.id);
  if (block.type === "heading") {
    return <h1 className={`sales-block sales-heading sales-align-${block.align || "left"}${selected}`} onClick={select}>{block.text}</h1>;
  }
  if (block.type === "paragraph") {
    return <p className={`sales-block sales-paragraph sales-align-${block.align || "left"}${selected}`} onClick={select}>{block.text}</p>;
  }
  if (block.type === "eyebrow") {
    return <p className={`sales-block sales-eyebrow sales-align-${block.align || "left"}${selected}`} onClick={select}>{block.text}</p>;
  }
  if (block.type === "image" && block.url) {
    return <img className={`sales-block sales-image${selected}`} src={block.url} alt={block.alt || ""} onClick={select} />;
  }
  if (block.type === "video" && block.url) {
    return <iframe className={`sales-block sales-video${selected}`} src={block.url} title={block.id} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />;
  }
  if (block.type === "button") {
    return (
      <div className={`sales-block sales-align-left${selected}`} onClick={select}>
        <a className="sales-button" href={block.url || "#checkout"}>{block.label}</a>
      </div>
    );
  }
  if (block.type === "checklist" || block.type === "badges") {
    return (
      <ul className={`sales-block ${block.type === "badges" ? "sales-badges" : "sales-list"}${selected}`} onClick={select}>
        {(block.items || []).map((item) => <li key={item}>{item}</li>)}
      </ul>
    );
  }
  if (block.type === "faq") {
    return (
      <div className={`sales-block sales-faq${selected}`} onClick={select}>
        {(block.items || []).map((item) => (
          <details key={item.question}>
            <summary>{item.question}</summary>
            <p>{item.answer}</p>
          </details>
        ))}
      </div>
    );
  }
  if (block.type === "offer_picker") {
    return (
      <div className={`sales-block sales-offers${selected}`} onClick={select}>
        {(block.items || [])
          .filter((item) => (offers || []).some((entry) => entry.code === item.offerCode))
          .map((item) => {
          const offer = offers.find((entry) => entry.code === item.offerCode);
          return (
            <button
              key={item.offerCode}
              type="button"
              className={`sales-offer${activeOffer === item.offerCode ? " is-active" : ""}`}
              onClick={(event) => {
                event.stopPropagation();
                onOffer(item.offerCode);
              }}
            >
              <strong>{item.label || offer?.name || item.offerCode}</strong>
              {offer ? <span>{money(offer.valueInCents)}</span> : null}
            </button>
          );
        })}
      </div>
    );
  }
  if (block.type === "row") {
    return (
      <div className={`sales-block sales-row${selected}`} style={{ "--sales-cols": String(block.columnCount || block.columns?.length || 2) }} onClick={select}>
        {(block.columns || []).map((column) => (
          <div key={column.id}>
            {(column.elements || []).map((child) => (
              <Block
                key={child.id}
                block={child}
                offers={offers}
                activeOffer={activeOffer}
                onOffer={onOffer}
                checkoutLinks={checkoutLinks}
                search={search}
                builder={builder}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }
  if (block.type === "checkout") {
    const code = checkoutLinks[activeOffer];
    return (
      <div id="checkout" className={`sales-block sales-checkout${selected}`} onClick={select}>
        {code ? (
          <CheckoutFrame code={code} search={search} builder={builder} />
        ) : (
          <div className="sales-checkout__placeholder">
            <strong>Checkout</strong>
            <p>Publish the page to load the payment form for this offer.</p>
          </div>
        )}
      </div>
    );
  }
  return null;
}

function CheckoutFrame({ code, search, builder }) {
  const [height, setHeight] = useState(720);
  const src = embedSrc(code, search);
  useEffect(() => {
    const onMessage = (event) => {
      const message = event.data;
      if (!message || message.source !== CHECKOUT_EMBED_SOURCE || message.type !== "height") return;
      const next = Number(message.height);
      if (next > 200) setHeight(Math.ceil(next));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);
  return (
    <div style={{ position: "relative" }}>
      <iframe title="Checkout" src={src} style={{ height }} />
      {builder ? <div style={{ position: "absolute", inset: 0 }} /> : null}
    </div>
  );
}

export default function SalesPage() {
  const { pathname, search } = useLocation();
  const slug = useMemo(() => {
    const match = pathname.match(/^\/p\/([^/]+)\/?$/);
    return match ? decodeURIComponent(match[1]) : "";
  }, [pathname]);
  const builder = useMemo(() => new URLSearchParams(search).get("builder") === "1", [search]);
  const requestedOffer = useMemo(() => new URLSearchParams(search).get("offer") || "", [search]);
  const [page, setPage] = useState(null);
  const [draft, setDraft] = useState(null);
  const [draftOffers, setDraftOffers] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [activeOffer, setActiveOffer] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(!builder);

  useEffect(() => {
    if (builder) return undefined;
    const controller = new AbortController();
    setLoading(true);
    fetch(`${salesPageApiBase()}/sales-pages/${encodeURIComponent(slug)}`, {
      signal: controller.signal,
      cache: "no-store",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("missing");
        return response.json();
      })
      .then((body) => {
        setPage(body.page);
        const allowed = new Set((body.page?.offers || []).map((offer) => offer.code));
        const initial = allowed.has(requestedOffer)
          ? requestedOffer
          : body.page?.defaultOfferCode || body.page?.offers?.[0]?.code || "";
        setActiveOffer(initial);
        document.title = body.page?.name || "AtomicPay";
      })
      .catch((requestError) => {
        if (requestError.name === "AbortError") return;
        setError("This page is not available.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [builder, requestedOffer, slug]);

  useEffect(() => {
    if (!builder) return undefined;
    let timer = 0;
    const onMessage = (event) => {
      const message = event.data;
      if (!message || message.source !== SALES_BUILDER_SOURCE) return;
      if (!parentOriginAllowed(event.origin)) return;
      if (message.type === "content" && message.content?.sections) {
        setDraft(message.content);
        setDraftOffers(Array.isArray(message.offers) ? message.offers : []);
        setPage((current) => ({
          ...(current || {}),
          name: message.name || current?.name || "",
          defaultOfferCode: message.defaultOfferCode || "",
          checkoutLinks: message.checkoutLinks || {},
          content: message.content,
          offers: Array.isArray(message.offers) ? message.offers : [],
        }));
        setActiveOffer((current) => current || message.defaultOfferCode || message.offers?.[0]?.code || "");
        setLoading(false);
        setError("");
      }
      if (message.selectedId != null) setSelectedId(message.selectedId);
    };
    window.addEventListener("message", onMessage);
    const notify = () => {
      window.parent?.postMessage({ source: SALES_BUILDER_SOURCE, type: "ready" }, "*");
    };
    notify();
    timer = window.setInterval(notify, 400);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("message", onMessage);
    };
  }, [builder]);

  const view = builder && draft ? { ...(page || {}), content: draft, offers: draftOffers || page?.offers || [] } : page;
  const settings = view?.content?.settings || {};
  const style = {
    "--sales-bg": settings.backgroundColor || "#ffffff",
    "--sales-text": settings.textColor || "#111827",
    "--sales-button": settings.buttonColor || "#111827",
    "--sales-button-text": settings.buttonTextColor || "#ffffff",
    "--sales-max": `${settings.maxWidth || 760}px`,
  };

  if (loading || (builder && !draft)) return <div className="sales-state">Loading page…</div>;
  if (error || !view?.content) return <div className="sales-state">{error || "This page is not available."}</div>;

  return (
    <main className="sales-page" style={style}>
      <div className="sales-page__inner">
        {(view.content.sections || []).map((section) => (
          <section key={section.id} className="sales-section" id={section.id}>
            {(section.elements || []).map((block) => (
              <Block
                key={block.id}
                block={block}
                offers={view.offers || []}
                activeOffer={activeOffer}
                onOffer={setActiveOffer}
                checkoutLinks={view.checkoutLinks || {}}
                search={search}
                builder={builder}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />
            ))}
          </section>
        ))}
      </div>
    </main>
  );
}
